/**
 * Socket room replay (permanent-session-continuity design §4.7).
 *
 * Records outgoing `join_*` emits (via socket.onAnyOutgoing), drops them on the
 * mapped `leave_*` event, and re-emits every recorded join on each 'connect'
 * after the first. No-op when the client lacks onAnyOutgoing (< socket.io-client 4.5).
 */

const MAX_RECORDS = 200;

/**
 * @param {object} socket socket.io-client Socket
 * @param {{ join?: string[], leaveMap?: Record<string,string> }} opts
 *   leaveMap maps a leave event to the join event it cancels.
 * @returns {() => void} uninstall
 */
export function installRoomReplay(socket, { join = [], leaveMap = {} } = {}) {
  try {
    if (!socket || typeof socket.onAnyOutgoing !== 'function' || typeof socket.on !== 'function') {
      return () => {};
    }
    const joinEvents = new Set(Array.isArray(join) ? join : []);
    const leaves = leaveMap && typeof leaveMap === 'object' ? leaveMap : {};
    /** @type {Map<string, Map<string, any[]>>} */
    const records = new Map();
    let replaying = false;
    let connectCount = 0;

    const keyOf = (args) => {
      try {
        return JSON.stringify(args?.[0] ?? null);
      } catch (e) {
        return String(args?.[0]);
      }
    };
    const totalRecords = () => {
      let n = 0;
      records.forEach((m) => { n += m.size; });
      return n;
    };

    const onOutgoing = (event, ...args) => {
      try {
        if (replaying) return;
        if (joinEvents.has(event)) {
          const key = keyOf(args);
          let m = records.get(event);
          if (!m) { m = new Map(); records.set(event, m); }
          if (!m.has(key) && totalRecords() >= MAX_RECORDS) return;
          m.set(key, args);
          return;
        }
        const target = leaves[event];
        if (target) records.get(target)?.delete(keyOf(args));
      } catch (e) {
        console.warn('[socketRoomReplay] record failed:', e?.message || e);
      }
    };

    const onConnect = () => {
      try {
        connectCount += 1;
        if (connectCount <= 1) return;
        replaying = true;
        records.forEach((m, event) => {
          m.forEach((args) => {
            try {
              socket.emit(event, ...args);
            } catch (e) {
              console.warn('[socketRoomReplay] replay emit failed:', e?.message || e);
            }
          });
        });
      } catch (e) {
        console.warn('[socketRoomReplay] replay failed:', e?.message || e);
      } finally {
        replaying = false;
      }
    };

    // A socket that is already connected has had its first connect.
    if (socket.connected) connectCount = 1;
    socket.onAnyOutgoing(onOutgoing);
    socket.on('connect', onConnect);

    return () => {
      try {
        if (typeof socket.offAnyOutgoing === 'function') socket.offAnyOutgoing(onOutgoing);
        if (typeof socket.off === 'function') socket.off('connect', onConnect);
      } catch (e) {
        console.warn('[socketRoomReplay] uninstall failed:', e?.message || e);
      }
    };
  } catch (e) {
    console.warn('[socketRoomReplay] install failed:', e?.message || e);
    return () => {};
  }
}
